// src/features/intentions/services/intentionsService.js
import {
  collection, addDoc, updateDoc, doc,
  query, where, orderBy, onSnapshot, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '../../../shared/utils/core/firebase-config';
import { getUserTimezone } from '../../../shared/utils/core/timezone';

/**
 * Compute ISO week ID (YYYY-WNN) from a Date anchored to the user's local timezone.
 * This prevents week boundaries from shifting at UTC midnight for users in UTC-offset zones.
 */
function getISOWeekId(date, tz) {
  // Resolve the local year/month/day in the user's timezone
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
  const parts = fmt.formatToParts(date);
  const year  = parseInt(parts.find(p => p.type === 'year')?.value ?? '1970', 10);
  const month = parseInt(parts.find(p => p.type === 'month')?.value ?? '1', 10) - 1;
  const day   = parseInt(parts.find(p => p.type === 'day')?.value ?? '1', 10);

  // ISO week arithmetic on the local date (treated as UTC to avoid DST shifts)
  const d = new Date(Date.UTC(year, month, day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
}

/** ISO week ID for today in the user's local timezone. */
export function getCurrentWeekId() {
  return getISOWeekId(new Date(), getUserTimezone());
}

/** ISO week ID for next week in the user's local timezone. */
export function getNextWeekId() {
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  return getISOWeekId(nextWeek, getUserTimezone());
}

/**
 * Firestore real-time listener for the current week's intentions PLUS any
 * pending intentions from prior weeks that the user hasn't acted on yet.
 *
 * Two subscriptions run in parallel:
 *   1. Current week (all statuses) — shows done/released completions for this week
 *   2. All pending from prior weeks — so intentions don't silently vanish when the week rolls over
 *
 * Returns a single combined unsubscribe function.
 */
export function subscribeToWeekIntentions(uid, weekId, onUpdate, onError) {
  let currentWeekDocs = [];
  let priorPendingDocs = [];

  function merge() {
    // Current week first, then prior-week pending (oldest first within each group)
    const ids = new Set();
    const combined = [];
    for (const d of currentWeekDocs) { ids.add(d.id); combined.push(d); }
    for (const d of priorPendingDocs) { if (!ids.has(d.id)) combined.push(d); }
    onUpdate(combined);
  }

  const currentWeekQ = query(
    collection(db, COLLECTIONS.INTENTIONS),
    where('uid', '==', uid),
    where('weekId', '==', weekId),
    orderBy('createdAt', 'asc'),
  );

  const priorPendingQ = query(
    collection(db, COLLECTIONS.INTENTIONS),
    where('uid', '==', uid),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'asc'),
  );

  const unsubCurrent = onSnapshot(
    currentWeekQ,
    (snap) => { currentWeekDocs = snap.docs.map(d => ({ id: d.id, ...d.data() })); merge(); },
    onError,
  );

  const unsubPrior = onSnapshot(
    priorPendingQ,
    (snap) => {
      // Exclude current week — already covered by the first subscription
      priorPendingDocs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(d => d.weekId !== weekId);
      merge();
    },
    onError,
  );

  return () => { unsubCurrent(); unsubPrior(); };
}

/**
 * Create a new intention.
 * @param {string} uid
 * @param {string} text
 * @param {string} weekId
 * @param {{ source?: 'manual'|'ai', sourceEntryId?: string }} [opts]
 */
export async function addIntention(uid, text, weekId, opts = {}) {
  const { source = 'manual', sourceEntryId = null } = opts;
  return addDoc(collection(db, COLLECTIONS.INTENTIONS), {
    uid,
    text: text.trim().slice(0, 200),
    weekId,
    status: 'pending',
    source,
    sourceEntryId,
    sourcePeriod: null,
    sourceSignalIds: null,
    carriedFromId: null,
    carriedCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: null,
  });
}

export async function updateIntentionStatus(intentionId, status) {
  return updateDoc(doc(db, COLLECTIONS.INTENTIONS, intentionId), {
    status,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Mark original as carried and create a new intention for the given weekId.
 * Uses a batch write so both operations succeed or fail atomically — the original
 * intention can never get stuck in 'carried' with no successor.
 */
export async function carryForwardIntention(intention, newWeekId) {
  const batch = writeBatch(db);

  // Mark original as carried
  const originalRef = doc(db, COLLECTIONS.INTENTIONS, intention.id);
  batch.update(originalRef, { status: 'carried', updatedAt: serverTimestamp() });

  // Create new intention for the upcoming week
  const newRef = doc(collection(db, COLLECTIONS.INTENTIONS));
  batch.set(newRef, {
    uid: intention.uid,
    text: intention.text,
    weekId: newWeekId,
    status: 'pending',
    source: intention.source,
    sourceEntryId: intention.sourceEntryId || null,
    sourcePeriod: intention.sourcePeriod || null,
    sourceSignalIds: intention.sourceSignalIds || null,
    carriedFromId: intention.id,
    carriedCount: (intention.carriedCount || 0) + 1,
    createdAt: serverTimestamp(),
    updatedAt: null,
  });

  await batch.commit();
  return newRef;
}
